import { useEffect, useRef } from 'react'
import { readClip, watchClip } from '@shared/services/projectClip'

/** How often to look, for a project opened in this same tab (no storage event then). */
const LOOK_MS = 1500

/**
 * Tell the page when a Copy to project has been dealt with.
 *
 * The copy waits in this browser until a project page takes it -- Add, or
 * No -- or a newer copy replaces it (src/shared/services/projectClip.ts).
 * Either way the page's "20 copied" message has done its job and should go
 * (Huanrui, 2026-09-30). A project opened in another tab is heard at once,
 * through the storage event; one opened in this tab is noticed by looking
 * every second and a half, which costs a read of one small local value.
 *
 * @param token the copy this page made, or empty when it is not waiting on one
 * @param onGone called once, when that copy is no longer the one waiting
 */
export function useClipSettled(token: string, onGone: () => void): void {
  const gone = useRef(onGone)
  gone.current = onGone

  useEffect(() => {
    if (!token) return
    let over = false
    const look = () => {
      if (over) return
      if (readClip()?.token === token) return
      over = true
      gone.current()
    }
    const stop = watchClip(look)
    const timer = setInterval(look, LOOK_MS)
    return () => {
      over = true
      stop()
      clearInterval(timer)
    }
  }, [token])
}
